import { useState, useEffect, useCallback } from 'react';
import type { GetProjectQueryData } from '@/generated/graphql';

type ProjectDetails = GetProjectQueryData['project'];
type ProjectVersion = ProjectDetails['versionHistory'][number];

/**
 * Hook to manage version selection state with automatic initialization
 * @param project - Project object with version history
 * @returns Object with selectedVersion, setSelectedVersion, and latestVersionNumber
 */
export function useVersionSelection(project: ProjectDetails | undefined) {
  const latestVersionNumber = project?.versionHistory?.length
    ? Math.max(...project.versionHistory.map((v: ProjectVersion) => v.version))
    : 1;

  const [selection, setSelection] = useState<{
    projectId: string;
    version: number;
  } | null>(null);
  const projectId = project?._id;
  // Derive the first selection during render, before consumers issue queries.
  // An effect-only initialization briefly requested version 1 on a deep link.
  const selectedVersion =
    selection?.projectId === projectId && selection
      ? selection.version
      : latestVersionNumber;
  useEffect(() => {
    if (projectId && project?.versionHistory?.length) {
      setSelection((current) =>
        current?.projectId === projectId
          ? current
          : { projectId, version: latestVersionNumber }
      );
    }
  }, [projectId, latestVersionNumber, project?.versionHistory?.length]);
  const setSelectedVersion = useCallback(
    (version: number) => {
      if (projectId) setSelection({ projectId, version });
    },
    [projectId]
  );

  return {
    selectedVersion,
    setSelectedVersion,
    latestVersionNumber,
  };
}
